import classNames from "classnames";
import { BanIcon } from "components/icons/BanIcon";
import {
  APPROACHING,
  EXEMPT,
  EXPOSED,
  MONITORING,
  REGISTERING,
  REMITTING,
} from "constants/nexusTrackerStatus";
import { FILING_INFO_MISSING } from "constants/salesTaxRegistrationStatusType";
import ToolTip from "components/design/toolTip";

const EXEMPTED_LABEL = "Not Required";

const STATUS_LABEL: Record<string, string> = {
  [MONITORING]: "Tracking",
  [APPROACHING]: "Approaching",
  [EXPOSED]: "Action Required",
  [REGISTERING]: "Registering",
  [REMITTING]: "Filing Active",
  [EXEMPT]: EXEMPTED_LABEL,
  [FILING_INFO_MISSING]: "Filing info missing",
};

const getToolTipText = (status: string, isExempted = false) => {
  if (isExempted) {
    return "No sales tax applicable for this state";
  }
  switch (status) {
    case MONITORING:
      return (
        <p className="t-m-0">
          Below threshold. <br /> Tracking progress actively
        </p>
      );
    case APPROACHING:
      return (
        <p className="t-m-0">
          Close to threshold (90%+).
          <br /> Consider pre-registering to ensure compliance
        </p>
      );
    case EXPOSED:
      return (
        <p className="t-m-0">
          Threshold exceeded. Registration required <br /> to avoid penalties
          and stay compliant
        </p>
      );
    case REGISTERING:
      return (
        <p className="t-m-0">
          Registration in progress. <br /> Check registration dashboard for
          status updates
        </p>
      );
    case REMITTING:
      return (
        <p className="t-m-0">
          Filing returns and remitting taxes.
          <br /> Check filing dashboard for more details
        </p>
      );
    case FILING_INFO_MISSING:
      return (
        <p className="t-m-0">
          Registration complete. <br /> Add filing details to start filing
        </p>
      );
    default:
      return "";
  }
};

export const NexusTrackerStatus = ({
  status,
  isExempted,
}: {
  status: string;
  isExempted?: boolean;
}) => {
  const label = isExempted ? EXEMPTED_LABEL : STATUS_LABEL[status] || status;

  return (
    <ToolTip text={getToolTipText(status, isExempted)} side="bottom">
      <div
        className={classNames(
          "t-max-w-fit t-text-body-sm t-text-center t-px-3 t-py-1 t-text-nowrap t-rounded-2xl",
          {
            "!t-bg-neutral-0 !t-text-neutral-70":
              status === MONITORING || isExempted,
          },
          {
            "t-bg-orange-10 t-text-orange-80": status === APPROACHING,
          },
          {
            "t-bg-blue-10 t-text-blue-70": status === REGISTERING,
          },
          {
            "t-bg-green-10 t-text-dark_green-70": status === REMITTING,
          },
          { "t-bg-red-10 t-text-red": status === EXPOSED },
          {
            "t-bg-orange-10 !t-text-orange-80": status === FILING_INFO_MISSING,
          }
        )}
      >
        {isExempted ? (
          <div className="t-flex t-items-center t-gap-1">
            <BanIcon />
            <span className="t-capitalize">{label}</span>
          </div>
        ) : (
          <span className="t-capitalize">{label}</span>
        )}
      </div>
    </ToolTip>
  );
};
